import { useEffect, useState } from "react";
import { AttributeType, type AttributeEntityType } from "../../../clients/Purchaser/Attributes";
import { Input, MenuRadioGroup, MenuRadioItem, MenuSection, stopKeyPropagation } from "../../DesignSystem";
import { type AttributeFilterOperator, type NumberAttributeFilter, type SetAttributeFilter } from "../types";

const OPERATOR_DISPLAY_MAP: Record<AttributeFilterOperator, string> = {
    eq: "Equal to",
    gt: "Greater than",
    lt: "Less than",
    gte: "Greater than or equal to",
    lte: "Less than or equal to",
    between: "Between",
};

const OPERATORS: AttributeFilterOperator[] = ["eq", "gt", "lt", "gte", "lte", "between"];

type NumberAttributeFilterSubmenuProps = {
    entityType: AttributeEntityType;
    attributeId: number;
    currentFilter?: NumberAttributeFilter;
    setAttributeFilter: SetAttributeFilter;
};

function NumberAttributeFilterSubmenu({ entityType, attributeId, currentFilter, setAttributeFilter }: NumberAttributeFilterSubmenuProps) {
    const [value, setValue] = useState<number | null>(currentFilter?.value ?? null);
    const [secondaryValue, setSecondaryValue] = useState<number | null>(currentFilter?.secondaryValue ?? null);
    const [operator, setOperator] = useState<AttributeFilterOperator>(currentFilter?.operator ?? "eq");
    const isBetween = operator === "between";

    useEffect(() => {
        if (isBetween && (value || value === 0) && (secondaryValue || secondaryValue === 0)) {
            return setAttributeFilter({
                type: AttributeType.NUMBER,
                attributeId,
                operator,
                value,
                secondaryValue,
            });
        }
        if (value || value === 0) {
            return setAttributeFilter({
                type: AttributeType.NUMBER,
                attributeId,
                operator,
                value,
            });
        }
        setAttributeFilter(
            {
                type: AttributeType.NUMBER,
                attributeId,
                operator,
            },
            true,
        );
    }, [value, secondaryValue, operator]);

    return (
        <>
            <MenuSection title="Filter by values that are...">
                <MenuRadioGroup value={operator} onChange={setOperator}>
                    {OPERATORS.map(op => (
                        <MenuRadioItem key={op} value={op}>
                            {OPERATOR_DISPLAY_MAP[op]}
                        </MenuRadioItem>
                    ))}
                </MenuRadioGroup>
            </MenuSection>
            <Input
                type="number"
                value={currentFilter?.value ?? ""}
                onChange={e => setValue(parseInt(e.target.value))}
                placeholder={isBetween ? "Enter a minimum value" : "Enter a value"}
                onKeyDown={stopKeyPropagation}
            />
            {isBetween && (
                <Input
                    type="number"
                    value={currentFilter?.secondaryValue ?? ""}
                    onChange={e => setSecondaryValue(parseInt(e.target.value))}
                    placeholder="Enter a maximum value"
                    onKeyDown={stopKeyPropagation}
                />
            )}
        </>
    );
}

export default NumberAttributeFilterSubmenu;
